import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { 
  LayoutGrid, 
  Package,
  PenSquare,
  LineChart,
  RefreshCcw,
  Settings,
  Bell,
  LogOut,
  User,
  Link
} from 'lucide-react';
import { useAuthStore } from './store/authStore';
import { SignIn } from './pages/SignIn';
import { CheckoutBuilder } from './pages/CheckoutBuilder';
import { Dashboard } from './pages/Dashboard';
import { Products } from './pages/Products';
import { Sales } from './pages/Sales';
import { Refunds } from './pages/Refunds';
import { AppSettings } from './pages/Settings';
import { CheckoutLinks } from './pages/CheckoutLinks';
import { CheckoutPreview } from './pages/CheckoutPreview';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { PaymentFailed } from './pages/PaymentFailed';
import { PageTransition } from './components/Layout/PageTransition';

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/signin" />;
};

const navItems = [
  { path: '/', icon: LayoutGrid, label: 'Dashboard' },
  { path: '/products', icon: Package, label: 'Products' },
  { path: '/checkout', icon: PenSquare, label: 'Build Checkout Page' },
  { path: '/links', icon: Link, label: 'Checkout Links' },
  { path: '/sales', icon: LineChart, label: 'Track Sales' },
  { path: '/refunds', icon: RefreshCcw, label: 'Manage Refunds' },
];

const DashboardLayout = ({ children }: { children: React.ReactNode }) => {
  const { user, logout } = useAuthStore();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#F8F9FD] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white flex flex-col border-r border-gray-100 fixed h-screen">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-gray-100">
          <span className="font-semibold text-xl text-[#1E293B]">Checkout Builder</span>
        </div>
        
        <div className="flex-1 px-3 py-6 overflow-y-auto">
          <p className="text-xs font-medium text-gray-400 px-3 mb-4">MAIN MENU</p>
          <nav className="space-y-1">
            {navItems.map(({ path, icon: Icon, label }) => (
              <a
                key={path}
                href={path}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-all rounded-lg ${
                  location.pathname === path 
                    ? 'bg-[#0F172A] text-white' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-[#0F172A]'
                }`}
              >
                <Icon className="w-5 h-5" />
                {label}
              </a>
            ))}
          </nav>

          <div className="mt-8 space-y-1">
            <a
              href="/settings"
              className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-all rounded-lg ${
                location.pathname === '/settings'
                  ? 'bg-[#0F172A] text-white'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-[#0F172A]'
              }`}
            >
              <Settings className="w-5 h-5" />
              Settings
            </a>
          </div>
        </div>

        {/* User Profile Section */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-2">
            <div className="w-10 h-10 rounded-full bg-[#1E293B] flex items-center justify-center">
              <User className="w-5 h-5 text-[#D4B86A]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{user?.name || 'Demo User'}</p>
              <p className="text-xs text-gray-500">{user?.email || 'demo@example.com'}</p>
            </div>
            <button 
              onClick={logout}
              className="p-1.5 text-gray-400 hover:text-[#1E293B] rounded-lg hover:bg-gray-50"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 ml-64">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-100 px-8 flex items-center justify-between fixed top-0 right-0 left-64 z-10">
          <h1 className="text-xl font-semibold text-[#1E293B]">
            {navItems.find(item => item.path === location.pathname)?.label || 'Settings'}
          </h1>
          <div className="flex items-center gap-6">
            <button className="p-2 text-gray-400 hover:text-[#1E293B] relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#D4B86A] rounded-full"></span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="pt-16">
          <PageTransition>
            {children}
          </PageTransition>
        </main>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/signin" element={<SignIn />} />
      <Route path="/checkout/:id" element={<CheckoutPreview />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/payment-failed" element={<PaymentFailed />} />
      
      {/* Private Routes */}
      <Route path="/" element={
        <PrivateRoute>
          <DashboardLayout>
            <Dashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />
      <Route path="/products" element={
        <PrivateRoute>
          <DashboardLayout>
            <Products />
          </DashboardLayout>
        </PrivateRoute>
      } />
      <Route path="/checkout" element={
        <PrivateRoute>
          <DashboardLayout>
            <CheckoutBuilder />
          </DashboardLayout>
        </PrivateRoute>
      } />
      <Route path="/links" element={
        <PrivateRoute>
          <DashboardLayout>
            <CheckoutLinks />
          </DashboardLayout>
        </PrivateRoute>
      } />
      <Route path="/sales" element={
        <PrivateRoute>
          <DashboardLayout>
            <Sales />
          </DashboardLayout>
        </PrivateRoute>
      } />
      <Route path="/refunds" element={
        <PrivateRoute>
          <DashboardLayout>
            <Refunds />
          </DashboardLayout>
        </PrivateRoute>
      } />
      <Route path="/settings" element={
        <PrivateRoute>
          <DashboardLayout>
            <AppSettings />
          </DashboardLayout>
        </PrivateRoute>
      } />
    </Routes>
  );
};

export default App;